import { useQueries } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'

import { useCompanyProjects } from '@/features/companies/api'
import { api } from '@/shared/api/client'
import { qk } from '@/shared/api/query-keys'
import { EmptyState, SkeletonRows } from '@/shared/components/data'
import { useI18n } from '@/shared/i18n'

import { KIND_ICON } from './components/kind-icons'
import { KIND_KEY, kindColor, type ComponentKind, type ProjectComponent } from './types'

interface StackEntry {
  tech: string
  version: string | null
  kind: ComponentKind
  projects: string[]
}

/**
 * Tech stack de la empresa.
 *
 * **No es una lista escrita a mano**: es la proyección de los componentes de
 * cada proyecto (plan 2.7). Una lista aparte se desactualiza el día que alguien
 * cambia la versión de Postgres en un proyecto y no se acuerda de venir acá.
 */
export function CompanyTechStackTab() {
  const { t, locale } = useI18n()
  const { companyId = '' } = useParams()
  const projects = useCompanyProjects(companyId)
  const items = projects.data ?? []

  const componentQueries = useQueries({
    queries: items.map((project) => ({
      queryKey: qk.projects.components(project.id),
      queryFn: () => api.get<ProjectComponent[]>(`projects/${project.id}/components`),
      staleTime: 60_000,
    })),
  })

  if (projects.isPending || componentQueries.some((query) => query.isPending)) {
    return <SkeletonRows rows={5} columns={[0.3, 0.2, 0.3]} withSpine={false} />
  }

  const byTech = new Map<string, StackEntry>()

  componentQueries.forEach((query, index) => {
    const projectName = items[index]?.name ?? ''
    for (const component of query.data ?? []) {
      const tech = component.tech ?? t(KIND_KEY[component.kind])
      const key = `${tech}@${component.version ?? ''}`
      const entry = byTech.get(key) ?? {
        tech,
        version: component.version,
        kind: component.kind,
        projects: [],
      }
      if (!entry.projects.includes(projectName)) entry.projects.push(projectName)
      byTech.set(key, entry)
    }
  })

  const stack = [...byTech.values()].sort(
    (a, b) => b.projects.length - a.projects.length || a.tech.localeCompare(b.tech, locale),
  )

  if (stack.length === 0) {
    return (
      <EmptyState
        title={t('architecture.stack.emptyTitle')}
        description={t('architecture.stack.emptyDescription')}
      />
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="text-2xs text-text-3">{t('architecture.stack.hint')}</p>

      <ul
        aria-label={t('architecture.stack.listLabel')}
        className="hairline flex flex-col overflow-hidden rounded-[var(--radius-card)] bg-surface-1"
      >
        {stack.map((entry) => {
          const Icon = KIND_ICON[entry.kind]
          return (
            <li
              key={`${entry.tech}@${entry.version ?? ''}`}
              className="hairline-b flex flex-wrap items-center gap-2 px-3 py-2 last:border-b-0"
            >
              <Icon size={13} aria-hidden="true" style={{ color: kindColor(entry.kind) }} />
              <span className="num text-xs text-text-1">{entry.tech}</span>
              {entry.version ? (
                <span className="num text-2xs text-text-3">{entry.version}</span>
              ) : null}
              <span className="text-2xs text-text-3">{t(KIND_KEY[entry.kind])}</span>
              <span className="flex-1" />
              <span
                title={t('architecture.stack.usedIn', { proyectos: entry.projects.join(' · ') })}
                className="truncate text-2xs text-text-2"
              >
                {entry.projects.join(' · ')}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
